import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export const TermsOfServiceDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="text-purple-600 hover:text-purple-700 underline bg-transparent border-none cursor-pointer">
          Terms of Service
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center mb-4">
            Mayl AI Terms of Use
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 text-sm leading-relaxed">
          <p className="text-center text-gray-600 font-medium">Last Revised: June 23, 2025</p>
          
          <div>
            <h3 className="font-semibold text-base mb-2">1. Introduction</h3>
            <p>These Terms of Use ("Terms") govern your use of the Mayl AI service ("Service"), operated by My People Know Inc., a Canadian corporation doing business as Mayl AI ("Mayl AI," "we," "us," or "our"). By registering for or using the Service, you ("you," "your," or "User") agree to these Terms. If you do not agree, do not use the Service.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">2. What the Service Does</h3>
            <p>Mayl AI is an AI-driven assistant that integrates with your Gmail account to automatically:</p>
            <ul className="list-disc ml-6 mt-2">
              <li>Apply and remove labels in your inbox</li>
              <li>Generate draft emails in your own style</li>
            </ul>
            <p className="mt-2">All AI functionality is powered exclusively by Google's Gemini models.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">3. Your Account & Access</h3>
            <p><strong>3.1 Google Sign-In Only.</strong> You must sign in via Google OAuth 2.0. We do not offer passwords or alternate login methods.</p>
            <p><strong>3.2 Credentials.</strong> You are responsible for safeguarding your Google credentials. Any activity under your account is deemed yours.</p>
            <p><strong>3.3 Scope of Access.</strong> We only request the Gmail scopes necessary to manage labels and compose drafts.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">4. License & Restrictions</h3>
            <p><strong>4.1 License Grant.</strong> We grant you a limited, non-exclusive, non-sublicensable license to use the Service for your internal email management.</p>
            <p><strong>4.2 What You May Not Do.</strong> You may not, under any circumstances:</p>
            <ul className="list-disc ml-6 mt-2">
              <li>Reverse-engineer or reconstruct the Service</li>
              <li>Share, resell, or sublicense the Service</li>
              <li>Extract data to build competing products</li>
              <li>Use the Service to process emails you are not authorized to access</li>
              <li>Inject malicious code or disrupt Service operations</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">5. Your Data & Privacy</h3>
            <p><strong>5.1 Data Collected.</strong> We store:</p>
            <ul className="list-disc ml-6 mt-2">
              <li>Your basic Google profile (name, email, avatar)</li>
              <li>Encrypted OAuth tokens for Gmail API calls</li>
              <li>Gmail label identifiers per user</li>
              <li>Brief, redacted snippets of your sent mail (only to analyze writing style; not retained long-term)</li>
              <li>Structured "persona profiles" derived from those snippets</li>
            </ul>
            <p className="mt-2"><strong>5.2 Data Handling.</strong></p>
            <ul className="list-disc ml-6 mt-2">
              <li>All tokens are encrypted at rest via AWS KMS.</li>
              <li>Session cookies are HTTP-Only, Secure, SameSite=Strict.</li>
              <li>We do not store full email contents or attachments.</li>
              <li>We comply with U.S. privacy laws and our public Privacy Policy.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">6. Intellectual Property</h3>
            <p><strong>6.1 Our Rights.</strong> Mayl AI and its licensors own all intellectual property rights in the Service, including the user interface, code, and documentation.</p>
            <p><strong>6.2 Your Rights.</strong> You retain ownership of your emails and credentials. By using the Service, you grant us the right to access and process your Gmail data solely to provide the Service.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">7. Disclaimers</h3>
            <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." MAYL AI DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. Google's Gemini models are probabilistic; results may vary and should be reviewed before sending.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">8. Limitation of Liability</h3>
            <p>TO THE EXTENT PERMITTED BY LAW, MAYL AI'S TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THIS AGREEMENT SHALL NOT EXCEED THE AMOUNTS YOU PAID IN THE SIX (6) MONTHS PRIOR TO THE CLAIM. IN NO EVENT SHALL MAYL AI BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">9. Indemnification</h3>
            <p>You agree to defend, indemnify, and hold Mayl AI harmless from any third-party claims arising out of your misuse of the Service or breach of these Terms.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">10. Termination</h3>
            <p><strong>10.1 By You.</strong> You may terminate your account at any time via your dashboard.</p>
            <p><strong>10.2 By Us.</strong> We may suspend or terminate your access immediately if you breach these Terms.</p>
            <p><strong>10.3 Data Deletion.</strong> Upon termination, we will delete your OAuth tokens, label mappings, and persona profiles within thirty (30) days, unless retention is required by law.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">11. Governing Law & Venue</h3>
            <p>These Terms are governed by the laws of the Province of Ontario, without regard to its conflict of laws rules. Any disputes shall be brought exclusively in the state or federal courts located in Toronto, Ontario, Canada.</p>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-2">12. Miscellaneous</h3>
            <p><strong>12.1 Entire Agreement.</strong> These Terms, together with our Privacy Policy, constitute the complete agreement.</p>
            <p><strong>12.2 Modifications.</strong> We may update these Terms periodically. We will post changes at least thirty (30) days in advance; continued use of the Service after changes constitutes acceptance.</p>
            <p><strong>12.3 Severability.</strong> If any provision is held invalid, the remainder of these Terms will remain in effect.</p>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold text-base mb-2">Contact Information</h3>
            <p>My People Know Inc. (dba Mayl AI)<br />
            100 King Street West, Suite: 5700, Toronto, Ontario, Canada, M5X 1C7<br />
            Email: sorted@mayl.ai</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
